import React from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";

interface ListLoadErrorProps {
  /** i18n key of the per-resource message, e.g. book_location.error.fetch_equipment. */
  messageKey: string;
  className?: string;
  /** A way onward — without one the customer has to find it themselves. */
  action?: { labelKey: string; onClick: () => void };
}

/**
 * What a list surface renders when its data could not be loaded (#1970).
 *
 * Without it these screens have no error state at all: the selection steps
 * derive "loading" from the list being empty, so a failed load leaves the
 * customer on a spinner that never resolves, and the apartment list reads as
 * "nothing available here". Same message as the notification the flow raises —
 * one wording per resource, in `book_location.error.*` — because this is the
 * copy that stays on screen after the toast is gone.
 *
 * Grayscale only: an error is not an accent, and tenant primary is the one
 * accent colour we have.
 */
const ListLoadError = ({ messageKey, className, action }: ListLoadErrorProps) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-10 text-center",
        className
      )}
    >
      <AlertCircle className="h-8 w-8 text-gray-400" aria-hidden="true" />
      <p className="text-sm text-gray-700">{t(messageKey)}</p>
      {action && (
        <Button variant="outline" className="mt-2" onClick={action.onClick}>
          {t(action.labelKey)}
        </Button>
      )}
    </div>
  );
};

export default ListLoadError;
